/**
 * HenniPenny — NILM / energy load-disaggregation workbench manifest.
 *
 * HenniPenny is the first real lab-branch on the Evidence Foundry tree. NILM appliance-type
 * disaggregation IS multi-class classification, so this field rides the shared field-agnostic
 * `WorkbenchShell` and the shared classification analyzer + `evidence.v1` spine — with zero
 * shell code changes. This manifest carries the field's copy; the rich run/results panels are
 * host-injected tab slots (`useNilmWorkbench`). Carries only energy-ML vocabulary.
 */
import type { WorkbenchManifest } from '../types';

export const ENERGY_NILM_WORKBENCH: WorkbenchManifest = {
  fieldId: 'energy.nilm',
  displayName: 'HenniPenny',
  eyebrow: 'NILM load-disaggregation evidence',
  branding: { title: 'HenniPenny — NILM load disaggregation' },
  tabs: [
    {
      id: 'disaggregation',
      label: 'Disaggregation',
      eyebrow: 'Predictions vs ground truth',
      description:
        'Join appliance-level predictions to a pinned benchmark on sample_id and score the disaggregation deterministically — precision, recall, F1, and a confusion matrix. Metrics, never the model.',
      experimentFamily: {
        title: 'Load-disaggregation experiments',
        body: 'These experiment families turn a labelled appliance-classification set into auditable evidence — figures of merit only, never a claim that the disaggregation model is fit to deploy.',
        cards: [
          {
            eyebrow: 'Active sample',
            title: 'Appliance-type classification',
            body: 'A synthetic 20-sample appliance-classification set (fridge, microwave, hairdryer, laptop) with a benchmark gate and a sealed report.',
            status: 'synthetic',
          },
          {
            eyebrow: 'Built analyzer',
            title: 'Class balance and support',
            body: 'Per-appliance support, imbalance flags, and minimum-support thresholds so a strong headline metric on a rare appliance is not over-read.',
            status: 'built',
          },
        ],
      },
      integrationRail: {
        title: 'Disaggregation tools and connections',
        body: 'These tools carry the evaluation set and its labels into the sealed run without letting a live prediction stand in for validation.',
        cards: [
          {
            eyebrow: 'Data source',
            title: 'Predictions and labels export',
            body: 'Paste the paired appliance predictions and benchmark ground-truth labels the deterministic analyzer scores.',
            status: 'live',
          },
          {
            eyebrow: 'Reproducibility',
            title: 'Sealed run and provenance',
            body: 'Every accepted result carries checksums, benchmark status, caveats, and reviewer decision space.',
            status: 'live',
          },
        ],
      },
    },
    {
      id: 'metrics',
      label: 'Precision · Recall · F1',
      eyebrow: 'Figures of merit',
      description:
        'Per-appliance and macro / weighted precision, recall, and F1 over the evaluated intersection, with class support and coverage gaps reported explicitly.',
      experimentFamily: {
        title: 'Classification figures of merit',
        body: 'Accuracy, macro and weighted precision/recall/F1, and a per-appliance one-vs-rest table — computed over the benchmark samples that have a prediction.',
        cards: [
          {
            eyebrow: 'Tool-computed',
            title: 'Macro and weighted metrics',
            body: 'Macro averages weight every appliance equally; weighted averages weight by support. Both are carried so the headline number is self-describing.',
            status: 'built',
          },
          {
            eyebrow: 'Reviewer role',
            title: 'Is it good enough?',
            body: 'Whether these numbers clear the bar for a home-energy deployment is a domain judgment; the workbench reports the metrics and leaves the call to the reviewer.',
            status: 'HITL',
          },
        ],
      },
    },
    {
      id: 'confusion',
      label: 'Confusion matrix',
      eyebrow: 'Error structure',
      description: 'The full confusion matrix behind the headline metrics — which appliances get mistaken for which.',
      experimentFamily: {
        title: 'Confusion-matrix breakdown',
        body: 'Where the errors actually fall — a fridge cycle read as a microwave, a hairdryer as a laptop — so aggregate metrics are read with their error structure, not in isolation.',
        cards: [
          {
            eyebrow: 'Built analyzer',
            title: 'Per-appliance confusion',
            body: 'Count view of the confusion matrix with the diagonal (correct) and the dominant off-diagonal confusions surfaced.',
            status: 'built',
          },
          {
            eyebrow: 'Reviewer role',
            title: 'Error-cost context',
            body: 'Which confusions matter is a domain judgment; the workbench reports the matrix and leaves the cost weighting to the reviewer.',
            status: 'HITL',
          },
        ],
      },
    },
    {
      id: 'provenance',
      label: 'Model & dataset provenance',
      eyebrow: 'What data, what model',
      description: 'The honest labels: which benchmark, which model run, and what the emitted evidence.v1 bundle does and does not claim.',
      integrationRail: {
        title: 'Provenance and honesty labels',
        body: 'The workbench keeps the dataset identity, the researcher-reported model boundary, and the evidence-bundle honesty machinery visible — not buried in a settings drawer.',
        cards: [
          {
            eyebrow: 'Dataset',
            title: 'Pinned benchmark labels',
            body: 'The synthetic sample ships a pinned benchmark; the real PLAID pin is carried by URL + SHA, not vendored.',
            status: 'synthetic',
          },
          {
            eyebrow: 'Trust root',
            title: 'Receipt slot stays empty',
            body: 'The emitted evidence.v1 bundle floors its verdict to inconclusive and leaves provenance.receipt null for a signer to fill later.',
            status: 'gated',
          },
        ],
      },
    },
  ],
};
